import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Theme, makeStyles } from '@material-ui/core/styles'
import {
    HARMONY_MENU_SHADOW,
    harmonyHoverColors,
    harmonyIconButtonBase,
} from 'shared/hooks/useHarmonyIconButtonStyles'

export const appHeaderHeight = 60
/** Height of the secondary action bar (DefaultActionBar) shown below the main nav when a page title is set. */
export const actionBarHeight = 60

export interface StyleProps {
    isStudioIconLoaded: boolean
}

export const useAppHeaderStyles = makeStyles((theme: Theme) => {
    return {
        fullHeight: {
            height: appHeaderHeight,
        },
        appHeaderContainer: {
            zIndex: theme.zIndex.appBar + 1,
            position: 'relative',
        },
        appBarContainer: {
            zIndex: theme.zIndex.appBar + 1,
            background: `linear-gradient(90.06deg, #2F4858 49.48%, #5F788A 100%)`,
            boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
            display: 'flex',
            flexDirection: 'row',
        },
        actionBar: {
            zIndex: theme.zIndex.appBar - 1,
            background: theme.palette.grey[700],
            transition: '100ms',
        },
        whiteFont: {
            color: theme.palette.common.white,
        },
        iconButton: {
            borderRadius: 0,
            opacity: 0.45,
            width: appHeaderHeight,
            transition: '200ms',

            '&:hover': {
                opacity: 1,
            },
        },
        reset: {
            padding: 0,
            backgroundColor: 'transparent',
            '&:hover': {
                backgroundColor: 'transparent',
            },
        },
        iconButtonActive: {
            backgroundColor: `rgba(0,0,0,0.25)`,
            opacity: 1,

            '&:hover': {
                backgroundColor: `rgba(0,0,0,0.25)`,
            },
        },
        hasDivider: {
            '&:after,&:before': {
                content: '""',
                width: 1,
                height: appHeaderHeight,
                backgroundColor: 'rgba(255,255,255,0.25)',

                '&:hover': {
                    backgroundColor: theme.palette.common.white,
                },
            },
        },
        appHeaderLeftContentContainer: {
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            height: '100%',
            maxWidth: pxToRem(580),
            [theme.breakpoints.down('xs')]: {
                width: 'unset',
            },
        },
        appHeaderLeftContainerHamburgerGap: {
            gap: theme.harmony.spacing.md,
        },
        fixedWidthContent: {
            width: 330,
        },
        appHeaderContentContainer: {
            height: '100%',
            paddingLeft: theme.spacing(2.6),
            flexWrap: 'nowrap',
        },
        appHeaderContentContainerLight: {
            display: 'grid',
            gridTemplateColumns: 'auto minmax(0, 1fr) auto',
            alignItems: 'center',
            paddingLeft: theme.harmony.spacing.md,
            paddingRight: theme.harmony.spacing.md,
        },
        middleContentLight: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            minWidth: 0,
            overflow: 'hidden',
        },
        fullPageMenuContainer: {
            zIndex: theme.zIndex.appBar,
            top: appHeaderHeight,
        },
        backdrop: {
            zIndex: theme.zIndex.appBar,
            backgroundColor: 'rgba(0,0,0,0.85)',
        },
        smallScreenWarningText: {
            color: theme.palette.common.white,
            textAlign: 'center',
        },
        warningCloseButton: {
            position: 'absolute',
            top: appHeaderHeight,
            right: 0,
        },
        deltekIcon: {
            '&.MuiSvgIcon-root': {
                width: pxToRem(230),
                fontSize: pxToRem(80),
            },
        },
        logoContainer: {
            paddingRight: theme.spacing(7),
            paddingLeft: theme.spacing(1),
            '@media (max-width: 1024px)': {
                paddingRight: theme.spacing(3),
            },
        },
        studioLoading: {
            padding: theme.spacing(2),
            '& .MuiCircularProgress-root': {
                color: '#b1abab',
            },
        },
        studioIcon: {
            display: (props: StyleProps) =>
                props.isStudioIconLoaded ? 'block' : 'none',
        },
        flex: {
            display: 'flex',
        },
        lightBulbMain: {
            height: appHeaderHeight,
            maxWidth: 50,
            opacity: 1,
            padding: 0,
        },
        lightBulbBtn: {
            height: '100%',
            borderRadius: 0,
            '&.Mui-focusVisible': {
                backgroundColor: theme.palette.action.focus,
            },
        },
        lightBulbIcon: {
            width: 25,
            height: 25,
            color: theme.palette.common.white,
            fill: 'none',
            stroke: 'currentColor',
            marginTop: -2,
        },
        lightBulbIconLight: {
            color: theme.harmony.colors.textHeader,
            width: pxToRem(22),
            height: pxToRem(22),
            '& path': {
                strokeWidth: 1.6,
            },
        },
        lightBulbMainLight: {
            height: 'auto',
            maxWidth: pxToRem(40),
        },
        lightBulbBtnLight: {
            ...harmonyIconButtonBase(theme),
            '&:hover': {
                ...harmonyHoverColors(theme),
                '& $lightBulbIconLight': {
                    color: theme.harmony.colors.brandMain,
                },
            },
        },
        appBarContainerLight: {
            background: theme.harmony.colors.headerBackground,
            // AppBar's primary color cascades white via currentColor; force dark so vendor icons stay visible.
            color: theme.harmony.colors.textHeader,
            boxShadow: `${theme.harmony.colors.panelShadow}, 0px 1px 0px ${theme.harmony.colors.shadow}`,
        },
        accentStripLight: {
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: pxToRem(-3),
            height: pxToRem(6),
            borderRadius: pxToRem(6),
            background: theme.harmony.colors.headerAccentGradient,
        },
        logoContainerLight: {
            paddingTop: pxToRem(4),
            paddingRight: pxToRem(12),
            paddingBottom: pxToRem(4),
            paddingLeft: 0,
            marginRight: theme.harmony.spacing.md,
        },
        logoImgLight: {
            height: pxToRem(30),
            width: 'auto',
            display: 'block',
        },
        leftClusterLight: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.harmony.spacing.md,
        },
        responsiveMenusLightOverride: {
            alignItems: 'center',
            gap: theme.harmony.spacing.md,
            minWidth: 0,
            // ResponsiveMenus (non-kpc variant) uses spacing=1 on its GridContainer, which
            // injects 4px padding on every GridItem — including the hidden notification/help
            // wrappers. Reset both the container's compensating negative-margin and child
            // padding so those hidden items don't create phantom horizontal space.
            '& .MuiGrid-spacing-xs-1': { margin: '0 !important' },
            '& .MuiGrid-spacing-xs-1 > .MuiGrid-item': { padding: '0 !important' },
            // HarmonyNotificationBell/HarmonyHelpButton replace these vendor triggers entirely
            // (custom glyphs the vendor doesn't support); hide them but keep the underlying
            // components mounted — Notification still needs its poll feeding the shared
            // userNotificationResult reactive var.
            '& [data-testid="globalNotificationButton"], & [data-testid="globalHelpButton"]': {
                display: 'none !important',
            },
            // The badge's hardcoded top offset was tuned for the vendor's larger original
            // button; reset it so MUI's default badge-corner anchoring applies instead.
            '& [data-testid="globalNotificationButton"] [class*="customBadge"]': {
                top: '0 !important',
                right: '0 !important',
            },
            '& [data-testid="globalUserDropdownButton"]:focus-visible': {
                outline: `2px solid ${theme.harmony.colors.focusRing} !important`,
                outlineOffset: '2px !important',
            },
            '& [data-testid="AvatarIconUserMenuDropdown"]': {
                display: 'flex !important',
                alignItems: 'center !important',
            },
            // Vendor's inner wrapper div gets height:100% and maxWidth:225px from minified prod classes;
            // flex keeps the button centered, maxWidth:none lets the pill grow with the user's name.
            // Transparent background suppresses the vendor's own bg-color visible on the light header.
            '& [data-testid="AvatarIconUserMenuDropdown"] > div': {
                display: 'flex !important',
                alignItems: 'center !important',
                maxWidth: 'none !important',
                backgroundColor: 'transparent !important',
            },
            '& [data-testid="globalUserDropdownButton"]': {
                color: `${theme.harmony.colors.textHeader} !important`,
                backgroundColor: 'transparent !important',
                border: 'none !important',
                borderRadius: '999px !important',
                height: '34px !important',
                minHeight: '0 !important',
                maxWidth: 'none !important',
                padding: '0 4px !important',
                transition: 'background 150ms ease',
            },
            '& [data-testid="globalUserDropdownButton"]:hover': {
                backgroundColor: `${theme.harmony.colors.hoverBg} !important`,
                color: `${theme.harmony.colors.brandMain} !important`,
            },
            // UserFirmsMenuDropdown hardcodes color:#fff, invisible on the light header.
            '& [data-testid="globalUserFirmDropdownButton"]': {
                color: `${theme.harmony.colors.textHeader} !important`,
            },
            // UserFirmsMenuDropdown's own open-state overlay assumes the dark AppBar; drop it.
            '& [class*="userFirmsMenuButton"][class*="activeDropdown"]': {
                backgroundColor: 'transparent !important',
            },
            // Keep the classic 8vw name truncation, just restyle it with Harmony's font.
            // [class*="..."] because MUI appends a random number to class names
            // (e.g. "MuiButton-label-123"), so an exact ".MuiButton-label" selector won't match.
            '& [data-testid="globalUserDropdownButton"] [class*="MuiButton-label"] span:not([class*="MuiButton-endIcon"])': {
                maxWidth: '8vw !important',
                display: 'inline-block !important',
                overflow: 'hidden !important',
                textOverflow: 'ellipsis !important',
                whiteSpace: 'nowrap !important',
                color: `${theme.harmony.colors.textHeader} !important`,
                fontFamily: `${theme.harmony.typography.tabLabel.fontFamily} !important`,
                fontSize: `${theme.harmony.typography.tabLabel.fontSize} !important`,
                fontWeight: `${theme.harmony.typography.tabLabel.fontWeight} !important`,
            },
            // Same specificity as the rule above, so without this the name text would stay
            // textHeader on hover/open instead of turning blue like the firm switcher does.
            '& [data-testid="globalUserDropdownButton"]:hover [class*="MuiButton-label"] span:not([class*="MuiButton-endIcon"])': {
                color: `${theme.harmony.colors.brandMain} !important`,
            },
            '& [data-testid="globalUserDropdownButton"][data-harmony-open] [class*="MuiButton-label"] span:not([class*="MuiButton-endIcon"])': {
                color: `${theme.harmony.colors.brandMain} !important`,
            },
            // Shrinks UserAvatar's default 32px image and widens its right margin to match the mockup's tighter pill spacing.
            '& [data-testid="profileImage"]': {
                width: '28px !important',
                height: '28px !important',
                margin: '1px 6px 1px 1px !important',
            },
            // Avatar-initials fallback (no photo): Harmony "12 Regular Lexend" on a solid brandMain swatch.
            '& [data-testid="profileAvatarIcon"]': {
                fontFamily: `${theme.harmony.typography.avatarInitials.fontFamily} !important`,
                fontSize: `${theme.harmony.typography.avatarInitials.fontSize} !important`,
                fontWeight: `${theme.harmony.typography.avatarInitials.fontWeight} !important`,
                color: '#FFFFFF !important',
                backgroundColor: `${theme.harmony.colors.brandMain} !important`,
            },
            '& [class*="userMenuButton"]': {
                borderRadius: '999px !important',
                height: '34px !important',
                display: 'inline-flex !important',
                alignItems: 'center !important',
                transition: 'background 150ms ease',
            },
            // Vendor's own margin override never fires (same numeric-suffix issue), so the
            // chevron falls back to MUI's default spacing — an 8px gap instead of AE's 4px.
            '& [data-testid="globalUserDropdownButton"] [class*="MuiButton-endIcon"]': {
                marginLeft: '4px !important',
                marginRight: '4px !important',
            },
            '& [data-testid="globalUserDropdownButton"][data-harmony-open]': {
                backgroundColor: `${theme.harmony.colors.activeBg} !important`,
                color: `${theme.harmony.colors.brandMain} !important`,
            },
            '& [data-testid="globalUserDropdownButton"][data-harmony-open] [class*="MuiButton-endIcon"] svg': {
                transform: 'rotate(180deg)',
            },
        },
        // The user-menu dropdown and the notification panel below both portal to document.body
        // via MUI, so a <body> class (toggled in AppHeader.tsx) is the only way to reach either.
        '@global': {
            // MUI v4 appends a random number to every class (e.g. "MuiListItemText-primary-1971"),
            // so exact ".MuiFoo" selectors never match. [class*="..."] matches the stable prefix instead.
            'body.harmonyHeaderActive [data-testid="globalPopoverUserMenu"] [class*="MuiMenu-paper"]': {
                borderRadius: '8px !important',
                boxShadow: `${HARMONY_MENU_SHADOW} !important`,
            },
            // Vendor's own 12px padding rule never fires (same numeric-suffix issue), so it
            // falls back to MUI's default (8px top/bottom, 0 left/right). Restore 12px here.
            'body.harmonyHeaderActive [data-testid="globalPopoverUserMenu"] [class*="MuiMenu-list"][class*="MuiList-padding"]': {
                padding: '12px !important',
            },
            // Same fix, for the help-menu popover. It has no wrapping data-testid, so target
            // it via ":has()" on its item testids instead (same pattern as ProjectReportsTable.tsx).
            'body.harmonyHeaderActive [class*="MuiMenu-list"][class*="MuiList-padding"]:has([data-testid^="globalHelpMenuItem"])': {
                padding: '12px !important',
            },
            'body.harmonyHeaderActive [data-testid^="userDropdownItem"] [class*="MuiListItemText-primary"]': {
                color: `${theme.harmony.colors.textHeader} !important`,
            },
            'body.harmonyHeaderActive [data-testid^="userDropdownItem"]:hover': {
                backgroundColor: `${theme.harmony.colors.hoverBg} !important`,
            },
            'body.harmonyHeaderActive [data-testid^="userDropdownItem"]:hover [class*="MuiListItemText-primary"]': {
                color: `${theme.harmony.colors.brandMain} !important`,
            },
            // The notification panel is a bare MUI Popover with no data-testid to target.
            // ":not(.MuiMenu-paper)" filters out the Menu-based popovers, but other bare
            // Popovers exist too (e.g. BPM's "More Filters"), so we also gate on
            // "harmonyNotificationOpen" (set in AppHeader.tsx only while this popover is open)
            // to avoid affecting those.
            'body.harmonyHeaderActive.harmonyNotificationOpen [class*="MuiPopover-paper"]:not([class*="MuiMenu-paper"])': {
                borderRadius: '8px !important',
                boxShadow: `${HARMONY_MENU_SHADOW} !important`,
            },
            // "Notifications" header — vendor hardcodes a classic dark blue.
            'body.harmonyHeaderActive.harmonyNotificationOpen [class*="MuiPopover-paper"]:not([class*="MuiMenu-paper"]) [class*="MuiListSubheader-root"]': {
                color: `${theme.harmony.colors.textHeader} !important`,
                fontFamily: `${theme.harmony.typography.tabLabel.fontFamily} !important`,
                borderBottomColor: `${theme.harmony.colors.strokeWeak} !important`,
            },
            // Regular notification text — vendor hardcodes the classic secondary color.
            'body.harmonyHeaderActive.harmonyNotificationOpen [class*="MuiPopover-paper"]:not([class*="MuiMenu-paper"]) [class*="MuiListItemText-primary"]': {
                color: `${theme.harmony.colors.brandMain} !important`,
            },
            // Date/secondary text — vendor hardcodes plain black.
            'body.harmonyHeaderActive.harmonyNotificationOpen [class*="MuiPopover-paper"]:not([class*="MuiMenu-paper"]) [class*="MuiListItemText-secondary"]': {
                color: `${theme.harmony.colors.textParagraph} !important`,
            },
            // Work-item notification title link — vendor hardcodes a one-off blue.
            'body.harmonyHeaderActive.harmonyNotificationOpen [class*="MuiPopover-paper"]:not([class*="MuiMenu-paper"]) [class*="secondaryContentTitle"]': {
                color: `${theme.harmony.colors.brandMain} !important`,
            },
            // "Clear All Notifications" / "View Release[s]" buttons — vendor hardcodes the classic brown/tan.
            'body.harmonyHeaderActive.harmonyNotificationOpen [class*="MuiPopover-paper"]:not([class*="MuiMenu-paper"]) [class*="MuiButton-contained"]': {
                backgroundColor: `${theme.harmony.colors.brandMain} !important`,
            },
            // hoverBg (an 8%-opacity tint meant for transparent/text elements) would wash this
            // solid button out to near-white; darken the existing fill instead, matching how
            // other solid Harmony buttons in this app handle hover (filter: brightness).
            'body.harmonyHeaderActive.harmonyNotificationOpen [class*="MuiPopover-paper"]:not([class*="MuiMenu-paper"]) [class*="MuiButton-contained"]:hover': {
                backgroundColor: `${theme.harmony.colors.brandMain} !important`,
                filter: 'brightness(0.92) !important',
            },
        },
    }
})
